import {
  Box,
  Button,
  FormHelperText,
  OutlinedInput,
  Stack,
  Typography,
  Link,
  CircularProgress,
} from '@mui/material';
import {Controller, useForm, useFormContext} from 'react-hook-form';
import React from 'react';
import {useDarkMode} from '../darkMode/hook/useDarkMode';
import {usePoliceMode} from '../policeMode/hook/usePoliceMode';
import states from './state';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

const RegistrationStep2 = ({handleNextStep, loading = false}) => {
  const {mode} = useDarkMode();
  const {police} = usePoliceMode();

  const {
    control,
    getValues,
    formState: {errors},
    handleSubmit,
  } = useFormContext();

  return (
    <Box sx={{backgroundColor: mode.settings.card4BackgroundColor, padding: 5}}>
      <Stack alignItems={'center'} spacing={2}>
        <Typography
          variant={'subtitle7'}
          sx={{color: mode.settings.textColor3}}>
          Créez votre compte
        </Typography>
        <Box width={'100%'}>
          <Stack>
            <Typography
              variant={'subtitle1'}
              sx={{color: mode.settings.textColor3}}
              component={'label'}
              id={'label-firstname'}
              htmlFor={'firstname'}>
              Prénom*
            </Typography>
            <Controller
              control={control}
              name="firstName"
              rules={{
                required: true,
              }}
              render={({field: {onChange, value}}) => (
                <OutlinedInput
                  size="small"
                  id="firstname"
                  aria-describedby="label-firstname"
                  inputProps={{
                    'aria-label': 'weight',
                  }}
                  type={'text'}
                  value={value}
                  style={{maxWidth: 570, width: '100%', height: 52}}
                  sx={{
                    background: mode.settings.cardBackgroundColor,
                    color: mode.settings.textColor3,
                  }}
                  onChange={onChange}
                  error={!!errors.firstName}
                />
              )}
            />
            <Typography
              variant={'subtitle1'}
              sx={{color: mode.settings.textColor3}}
              component={'label'}
              id={'label-lastname'}
              htmlFor={'lastname'}>
              Nom*
            </Typography>
            <Controller
              control={control}
              name="lastName"
              rules={{
                required: true,
              }}
              render={({field: {onChange, value}}) => (
                <OutlinedInput
                  size="small"
                  id="lastname"
                  aria-describedby="label-lastname"
                  inputProps={{
                    'aria-label': 'weight',
                  }}
                  type={'text'}
                  value={value}
                  style={{maxWidth: 570, width: '100%', height: 52}}
                  sx={{
                    background: mode.settings.cardBackgroundColor,
                    color: mode.settings.textColor3,
                  }}
                  onChange={onChange}
                  error={!!errors.lastName}
                />
              )}
            />
            <Typography
              variant={'subtitle1'}
              sx={{color: mode.settings.textColor3, marginTop: 2}}
              component={'label'}
              id={'label-birthday'}
              htmlFor={'birthday'}>
              Date de naissance*
            </Typography>
            <Controller
              control={control}
              name="birthDate"
              rules={{
                required: true,
                validate: value => {
                  // must be recent than 1900
                  if (new Date(value) < new Date('1910-01-01')) {
                    return 'La date de naissance ne peut pas être antérieur à 1910.';
                  }

                  const date = new Date(value);
                  const now = new Date();
                  if (date > now) {
                    return 'La date de naissance ne peut pas être supérieure à la date actuelle.';
                  }
                  return true;
                },
              }}
              render={({field: {onChange, value}}) => (
                <OutlinedInput
                  min="1900-01-01"
                  size="small"
                  id="birthday"
                  aria-describedby="label-birthday"
                  inputProps={{
                    'aria-label': 'weight',
                  }}
                  type={'date'}
                  value={value}
                  style={{maxWidth: 570, width: '100%', height: 52}}
                  sx={{
                    background: mode.settings.cardBackgroundColor,
                    color: mode.settings.textColor3,
                  }}
                  onChange={onChange}
                  error={!!errors.birthDate}
                />
              )}
            />
            {!!errors.birthDate && (
              <FormHelperText error>{errors.birthDate.message}</FormHelperText>
            )}
            <Typography
              variant={'subtitle1'}
              sx={{color: mode.settings.textColor3}}
              component={'label'}
              id={'label-country'}
              htmlFor={'country'}>
              Pays*
            </Typography>
            <Controller
              control={control}
              name="countryCode"
              rules={{
                required: true,
              }}
              defaultValue={'FR'}
              render={({field: {onChange, value}}) => (
                <Select
                  labelId="label-country"
                  id="country"
                  value={value}
                  label="Pays"
                  onChange={e => onChange(e.target.value)}
                  error={!!errors.states}
                  sx={{
                    background: mode.settings.cardBackgroundColor,
                    color: mode.settings.textColor3,
                  }}
                  displayEmpty>
                  {states &&
                    Object.entries(states).map((option, index) => (
                      <MenuItem
                        key={index}
                        value={option[0]}
                        selected={option[1] === 'France'}>
                        {option[1]}
                      </MenuItem>
                    ))}
                </Select>
              )}
            />
            <Typography
              variant={'subtitle1'}
              sx={{color: mode.settings.textColor3}}
              component={'label'}
              id={'label-address'}
              htmlFor={'address'}>
              Adresse Postale*
            </Typography>
            <Controller
              control={control}
              name="address"
              rules={{
                required: true,
              }}
              render={({field: {onChange, value}}) => (
                <OutlinedInput
                  size="small"
                  id="address"
                  aria-describedby="label-address"
                  inputProps={{
                    'aria-label': 'weight',
                  }}
                  type={'text'}
                  value={value}
                  style={{maxWidth: 570, width: '100%', height: 52}}
                  sx={{
                    background: mode.settings.cardBackgroundColor,
                    color: mode.settings.textColor3,
                  }}
                  onChange={onChange}
                  error={!!errors.address}
                  placeholder={'n° et nom de rue'}
                />
              )}
            />

            <Typography
              variant={'subtitle1'}
              sx={{color: mode.settings.textColor3}}
              component={'label'}
              id={'label-address-complement'}
              htmlFor={'address-complement'}>
              Complément d'adresse
            </Typography>
            <Controller
              control={control}
              name="complement"
              render={({field: {onChange, value}}) => (
                <OutlinedInput
                  size="small"
                  id="address-complement"
                  aria-describedby="label-address-complement"
                  inputProps={{
                    'aria-label': 'weight',
                  }}
                  type={'text'}
                  value={value}
                  style={{maxWidth: 570, width: '100%', height: 52}}
                  sx={{
                    background: mode.settings.cardBackgroundColor,
                    color: mode.settings.textColor3,
                  }}
                  onChange={onChange}
                  error={!!errors.complement}
                  placeholder={'appartement, étage, bâtiment'}
                />
              )}
            />

            <Typography
              variant={'subtitle1'}
              sx={{color: mode.settings.textColor3}}
              component={'label'}
              id={'label-postal-code'}
              htmlFor={'postal-code'}>
              Code Postal*
            </Typography>
            <Controller
              control={control}
              name="postalCode"
              rules={{
                required: true,
              }}
              render={({field: {onChange, value}}) => (
                <OutlinedInput
                  size="small"
                  id="postal-code"
                  aria-describedby="label-postal-code"
                  inputProps={{
                    'aria-label': 'weight',
                  }}
                  type={'text'}
                  value={value}
                  style={{maxWidth: 570, width: '100%', height: 52}}
                  sx={{
                    background: mode.settings.cardBackgroundColor,
                    color: mode.settings.textColor3,
                  }}
                  onChange={onChange}
                  error={!!errors.postalCode}
                />
              )}
            />
            <Typography
              variant={'subtitle1'}
              sx={{color: mode.settings.textColor3}}
              component={'label'}
              id={'label-city'}
              htmlFor={'city'}>
              Ville*
            </Typography>
            <Controller
              control={control}
              name="city"
              rules={{
                required: true,
              }}
              render={({field: {onChange, value}}) => (
                <OutlinedInput
                  size="small"
                  id="city"
                  aria-describedby="label-city"
                  inputProps={{
                    'aria-label': 'weight',
                  }}
                  type={'text'}
                  value={value}
                  style={{maxWidth: 570, width: '100%', height: 52}}
                  sx={{
                    background: mode.settings.cardBackgroundColor,
                    color: mode.settings.textColor3,
                  }}
                  onChange={onChange}
                  error={!!errors.city}
                />
              )}
            />
          </Stack>
        </Box>
        <Stack width={'100%'}>
          <Controller
            control={control}
            name="cgv"
            rules={{
              required: true,
            }}
            render={({field: {onChange, value}}) => (
              <div>
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  flexWrap={'wrap'}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        onChange={onChange}
                        sx={{'& .MuiSvgIcon-root': {fontSize: 28}}}
                        helperText={''}
                      />
                    }
                  />
                  <Typography
                    variant={'subtitle5'}
                    sx={{color: mode.settings.textColor3}}>
                    J'accepte les &nbsp;
                  </Typography>
                  <Typography variant={'subtitle5'} sx={{color: '#2F749F'}}>
                    <a
                      style={{color: '#2F749F'}}
                      href={
                        'http://ohs-solutions.fr/politique-de-confidentialite/'
                      }
                      target="_blank">
                      conditions générales de vente
                    </a>
                  </Typography>
                  <Typography
                    variant={'subtitle5'}
                    sx={{color: mode.settings.textColor3}}>
                    &nbsp; et les &nbsp;
                  </Typography>
                  <Typography variant={'subtitle5'} sx={{color: '#2F749F'}}>
                    <a
                      style={{color: '#2F749F'}}
                      href={'http://ohs-solutions.fr/mentions-legales/'}
                      target="_blank">
                      conditions générales d'utilisation
                    </a>
                  </Typography>
                </Stack>
                {!!errors.cgv && (
                  <FormHelperText error sx={{marginTop: '-10px'}}>
                    Veuillez accepter les générales de vente et les conditions
                    générales d'utilisation.
                  </FormHelperText>
                )}
              </div>
            )}
          />
        </Stack>

        <br />
        <Button
          className="btnPink"
          size={'large'}
          role="presentation"
          sx={{width: '300px', position: 'relative'}}
          onClick={() => handleNextStep && handleNextStep()}
          disabled={loading}>
          {loading && (
            <CircularProgress
              color="inherit"
              size={30}
              sx={{position: 'absolute', top: 10, left: 15}}
            />
          )}
          <Typography variant={'subtitle1'}>Créer un compte</Typography>
        </Button>
      </Stack>
    </Box>
  );
};

export default RegistrationStep2;
